import * as angular from 'angular';
import * as _ from 'lodash';

import { ComponentDefinition, PropertyType, ContextualField, AggregationMode, NameBuilderConfiguration, TrendLogbookEntriesType } from '../../../models';
import * as Fields from '../../../common/helpers/contextual';
import { TagsTrendConfiguration } from '../../../models/widgets/TagsTrendConfiguration/tagsTrendConfiguration';
import { backwardCompatibility } from '../../../common';
import { IGroupSelectionService } from '../../../services';
class TagsTrendContextualController implements angular.IComponentController {
    cancel: () => void;
    configuration: TagsTrendConfiguration;
    formController: angular.IFormController;
    isLoading: boolean;
    loadingMessage: string;
    properties: ContextualField[];
    save: () => void;

    private _originalConfiguration: TagsTrendConfiguration;
    private garbages: (() => void)[] = [];

    static $inject = [
        'groupSelectionService',
        '$scope',
        '$q'
    ];

    constructor(
        private _groupSelectionService: IGroupSelectionService,
        private _$scope: angular.IScope,
        private _$q: angular.IQService,
    ) { }

    $onInit() {
        this.isLoading = true;

        this._originalConfiguration = angular.copy(this.configuration);
        this.initProperties();

        this.garbages.push(this._$scope.$watch(
            () => this.configuration,
            () => this.onConfigurationChange(),
            true
        ));

        this.isLoading = false;
    }

    $onDestroy() {
        this.garbages.forEach(g => g());
    }

    onSave(): angular.IPromise<void> {
        return this._$q.when(this.save());
    }

    onCancel() {
        this.configuration = angular.copy(this._originalConfiguration);
        this.cancel();
    }

    private initProperties() {
        if (!this.configuration.nameConfiguration) {
            this.configuration.nameConfiguration = new NameBuilderConfiguration(false);
        }
        if (this.configuration.alignedOn === undefined) {
            this.configuration.alignedOn = 0;
        }
        const showAlarms = angular.copy(Fields.ShowAlarms);
        showAlarms.disabled = this.configuration.series.filter(s => s.offset?.interval).length > 0;

        const boundDisabled = this._groupSelectionService.selectedGroup() === null || this._groupSelectionService.selectedGroup() === undefined;
        const groupGuidsProperty = Object.assign({}, Fields.GroupGuids, {
            required: false,
            disabled: this.configuration.isBoundToGroup,
            hide: this.hideLogbookOption(this.configuration.logbookEntriesShownType, this.configuration.showLogbook),
        });
        const groupTypesProperty = angular.copy(Fields.GroupTypes);
        groupTypesProperty.disabled = this.configuration.isBoundToGroup;
        groupTypesProperty.hide = this.hideLogbookOption(this.configuration.logbookEntriesShownType, this.configuration.showLogbook);
        const includeChildrenProperty = angular.copy(Fields.IncludeChildren);
        includeChildrenProperty.hide = this.hideLogbookOption(this.configuration.logbookEntriesShownType, this.configuration.showLogbook);

        const logbookEntriesFilters = [
            //
            //LBE filters
            //
            {
                modelName: 'isBoundToGroup',
                propertyType: PropertyType.Boolean,
                label: 'Bind',
                placeholder: 'Bind',
                propertyName: 'isBoundToGroupProperty',
                required: false,
                icon: 'fa-anchor',
                disabled: boundDisabled,
                extraData: {
                },
                hide: this.hideLogbookOption(this.configuration.logbookEntriesShownType, this.configuration.showLogbook),
                onChange: (value: boolean) => {
                    groupGuidsProperty.disabled = value;
                    groupTypesProperty.disabled = value;
                }
            },
            includeChildrenProperty,
            groupGuidsProperty,
            groupTypesProperty,
            {
                modelName: 'severities',
                propertyType: PropertyType.Severities,
                label: 'Severity',
                propertyName: 'severitiesProperty',
                required: false,
                icon: 'fa-bell',
                hide: this.hideLogbookOption(this.configuration.logbookEntriesShownType, this.configuration.showLogbook),
                extraData: {
                }
            }, {
                modelName: 'categories',
                propertyType: PropertyType.Categories,
                label: 'Categories',
                propertyName: 'categoriesProperty',
                required: false,
                icon: 'fa-tags',
                hide: this.hideLogbookOption(this.configuration.logbookEntriesShownType, this.configuration.showLogbook),
                extraData: {
                }
            }
        ];

        const logbookEntriesShown: ContextualField = {
            modelName: 'logbookEntriesShownType',
            propertyType: PropertyType.Select,
            icon: ['fa-book', 'fa-eye'],
            label: 'Logbook entries shown',
            propertyName: 'logbookEntriesShownTypeProperty',
            extraData: {
                options: [
                    { key: TrendLogbookEntriesType.Default, value: 'Logbook entries linked to series' },
                    { key: TrendLogbookEntriesType.Custom, value: 'Customized' },
                ],
                defaultValue: TrendLogbookEntriesType.Default
            },
            hide: !this.configuration.showLogbook,
            onChange: (viewValue, extraAgrs) => {
                logbookEntriesFilters.forEach(p => { p.hide = this.hideLogbookOption(viewValue, this.configuration.showLogbook); });
            }
        };

        const allowLogbookEntries = angular.copy(Fields.AllowLogbookEntries);
        allowLogbookEntries.onChange = (viewValue, extraArgs) => {
            logbookEntriesShown.hide = !viewValue;
            logbookEntriesFilters.forEach(p => { p.hide = this.hideLogbookOption(this.configuration.logbookEntriesShownType, viewValue); });
        };

        this.properties = [
            angular.copy(Fields.Title),
            angular.copy(Fields.IsTitleVisible),
            angular.copy(Fields.IsLegendVisible),
            angular.copy(Fields.IsHorizontalGridLineVisible),
            {
                modelName: 'showXAxis',
                propertyType: PropertyType.Boolean,
                label: 'Show X-axis',
                placeholder: 'Show X-axis',
                propertyName: 'showXAxisProperty',
                iconText: '\u21a6',
                extraData: {
                    subProperty: 'options'
                }
            },
            {
                modelName: 'showYAxis',
                propertyType: PropertyType.Boolean,
                label: 'Show Y-axis',
                placeholder: 'Show Y-axis',
                propertyName: 'showYAxisProperty',
                iconText: '\u21a5',
                extraData: {
                    subProperty: 'options'
                }
            },
            {
                modelName: 'yMin',
                propertyType: PropertyType.Number,
                label: 'Y min',
                placeholder: 'Y min',
                propertyName: 'yMinProperty',
                required: false,
                icon: 'fa-long-arrow-down',
                extraData: {
                    subProperty: 'options.display',
                    decimals: 0,
                    options: {
                        updateOn: 'default blur',
                        debounce: { 'default': 500, 'blur': 0 }
                    }
                }
            },
            {
                modelName: 'yMax',
                propertyType: PropertyType.Number,
                label: 'Y max',
                placeholder: 'Y max',
                propertyName: 'yMaxProperty',
                required: false,
                icon: 'fa-long-arrow-up',
                extraData: {
                    subProperty: 'options.display',
                    decimals: 0,
                    options: {
                        updateOn: 'default blur',
                        debounce: { 'default': 500, 'blur': 0 }
                    }
                }
            },
            angular.copy(Fields.AggregationMode),
            Fields.AggregationPeriodBuilder(this.configuration),
            angular.copy(Fields.MinPeriod),
            angular.copy(Fields.MaxPeriod),
            angular.copy(Fields.PeriodReference),
            angular.copy(Fields.IsEndDateIncluded),
            {
                modelName: 'hideMissingRanges',
                propertyType: PropertyType.Boolean,
                label: 'Hide missing ranges',
                placeholder: 'Hide missing ranges',
                propertyName: 'hideMissingRangesProperty',
                required: false,
                icon: 'fa-eye-slash'
            },
            angular.copy(Fields.NameConfiguration.HasGroupName),
            angular.copy(Fields.NameConfiguration.HasName),
            angular.copy(Fields.NameConfiguration.HasShortComment),
            angular.copy(Fields.NameConfiguration.HasUnit),
            angular.copy(Fields.NameConfiguration.HasValueType),
            {
                modelName: 'hasOffset',
                propertyType: PropertyType.Boolean,
                label: 'Offset',
                placeholder: 'Offset',
                propertyName: 'hasOffsetProperty',
                required: false,
                icon: 'fa-arrows-h',
                extraData: { subProperty: 'nameConfiguration' }
            },
            angular.copy(Fields.PresentationUnit),
            showAlarms,
            allowLogbookEntries,
            logbookEntriesShown,
        ]
            .concat(logbookEntriesFilters);
    }

    private hideLogbookOption = (showType: TrendLogbookEntriesType, showLogbook: boolean) => {
        return showType === TrendLogbookEntriesType.Default || !showType || !showLogbook ? true : false;
    }


    private changeShowLogbookEntriesType() {

        const toHide = ['groupGuidsProperty', 'groupTypesProperty', 'dateOrderProperty', 'sortByProperty', 'categoriesProperty', 'severitiesProperty', 'isAlwaysShowProperty', 'includeChildrenProperty', 'isBoundToGroupProperty'];
        toHide.forEach(x => {
            const typeProperty = _.find(this.properties, p => p.propertyName === x);
            typeProperty.hide = this.hideLogbookOption(this.configuration.logbookEntriesShownType, this.configuration.showLogbook);
        });
    }

    private changeShowLogbookEntries() {
        const logbookEntriesShownTypeProperty = _.find(this.properties, p => p.propertyName === 'logbookEntriesShownTypeProperty');
        if (logbookEntriesShownTypeProperty) {
            logbookEntriesShownTypeProperty.hide = !this.configuration.showLogbook;
        }
    }

    private onConfigurationChange() {
        // Backward compat to set true on previous widget
        backwardCompatibility(this.configuration?.options, 'showXAxis', true);
        backwardCompatibility(this.configuration?.options, 'showYAxis', true);

        const aggregationPeriodProperty = _.find(this.properties, p => p.propertyName === 'aggregationPeriodProperty');
        if (aggregationPeriodProperty) {
            aggregationPeriodProperty.disabled = this.configuration.aggregation.mode === AggregationMode.None;
        }

        const alarmsField = _.find(this.properties, p => p.propertyName === 'showAlarmsProperty');
        if (alarmsField) {
            alarmsField.disabled = this.configuration.series.filter(s => s.offset && s.offset.interval).length > 0;
            if (alarmsField.disabled) {
                this.configuration.showAlarms = false;
            }
        }
        this.changeShowLogbookEntriesType();
        this.changeShowLogbookEntries();
    }
}

export const wtTagsTrendContextual: ComponentDefinition = {
    componentId: 'wtTagsTrendContextual',
    controller: TagsTrendContextualController,
    template: require('./tagsTrendContextual.component.html'),
    controllerAs: 'vm',
    bindings: {
        formController: '=',
        configuration: '=',
        save: '&?',
        cancel: '&?'
    }
};
